import { Button } from "@/components/ui/button";
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { AdminPageProps, AuthenticatedUserProp, ProductType } from "@/types";
import { useForm, usePage } from "@inertiajs/react";
import { FormEvent } from "react";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import { Spinner } from "@/components/ui/spinner";
import { store_products_path } from "@/lib/routes";
import { getCurrencySymbol } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SheetProps } from "@/types/ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CatalogSearchField from "./catalog-search-field";
import useTranslations from "@/hooks/use-translations";

type NewProductData = {
  product: {
    name: null | string;
    product_type: ProductType | string;
    system_code: string;
    color: string;
    state: string;
    price: number;
    price_cents: number;
    stock: number;
    custom_image: null | File;
    catalogable_id: null | number;
  };
};

export default function NewProductSheet({ open, onOpenChange }: SheetProps) {
  const { t } = useTranslations("shared.components.new_product_sheet");
  const newProductForm = useForm<NewProductData>({
    product: {
      name: null,
      product_type: "set" satisfies ProductType,
      system_code: "",
      color: "",
      state: "new_sealed",
      price: 0,
      stock: 0,
      price_cents: 0,
      custom_image: null,
      catalogable_id: null,
    },
  });
  const { data, setData } = newProductForm;
  const { current_store, allowed_product_types } = usePage<
    AdminPageProps & AuthenticatedUserProp
  >().props;

  function onTabSelected(value: string) {
    setData("product.product_type", value);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    newProductForm.post(store_products_path(current_store!), {
      invalidateCacheTags: ["stores", current_store?.id ?? ""],
      onSuccess() {
        newProductForm.resetAndClearErrors();
        onOpenChange(false);
      },
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full md:w-3/4">
        <SheetHeader>
          <SheetTitle>{t("title")}</SheetTitle>
        </SheetHeader>

        <form
          id="new-product-form"
          method="post"
          onSubmit={onSubmit}
          className="flex-1 flex flex-col gap-5 px-4 overflow-auto"
        >
          <FieldGroup>
            <Tabs
              value={data.product.product_type}
              onValueChange={onTabSelected}
              className="gap-5"
            >
              <TabsList className="w-full">
                <TabsTrigger
                  value="set"
                  disabled={!allowed_product_types.includes("set")}
                >
                  {t("tab_set")}
                </TabsTrigger>
                <TabsTrigger
                  value="minifigure"
                  disabled={!allowed_product_types.includes("minifigure")}
                >
                  {t("tab_minifigure")}
                </TabsTrigger>
                <TabsTrigger
                  value="part"
                  disabled={!allowed_product_types.includes("part")}
                >
                  {t("tab_part")}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="set">
                <CatalogSearchField
                  productType={data.product.product_type as ProductType}
                  onSelectedSystemCode={(newValues) =>
                    setData((values) => ({
                      product: { ...values.product, ...newValues },
                    }))
                  }
                />
              </TabsContent>
              <TabsContent value="minifigure">
                <CatalogSearchField
                  productType={data.product.product_type as ProductType}
                  onSelectedSystemCode={(newValues) =>
                    setData((values) => ({
                      product: { ...values.product, ...newValues },
                    }))
                  }
                />
              </TabsContent>
              <TabsContent value="part">
                <CatalogSearchField
                  productType={data.product.product_type as ProductType}
                  onSelectedSystemCode={(newValues) =>
                    setData((values) => ({
                      product: { ...values.product, ...newValues },
                    }))
                  }
                />
              </TabsContent>
            </Tabs>

            <FieldSeparator />

            <FieldSet>
              <Field>
                <FieldLabel htmlFor="state">
                  {t("shared.fields.state")}
                </FieldLabel>
                <Select
                  name="product[state]"
                  defaultValue="new_sealed"
                  value={data.product.state}
                  onValueChange={(value) => setData("product.state", value)}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("shared.fields.state_placeholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new_sealed">
                      {t("shared.fields.state_new_sealed")}
                    </SelectItem>
                    <SelectItem value="new_complete">
                      {t("shared.fields.state_new")}
                    </SelectItem>
                    <SelectItem value="pre_owned">
                      {t("shared.fields.state_pre_owned")}
                    </SelectItem>
                    <SelectItem value="pre_owned_with_box">
                      {t("shared.fields.state_pre_owned_with_box")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel htmlFor="price">
                  {t("shared.fields.price")}
                </FieldLabel>

                <InputGroup>
                  <InputGroupAddon>
                    <InputGroupText>
                      {getCurrencySymbol(current_store?.currency ?? "usd")}
                    </InputGroupText>
                  </InputGroupAddon>
                  <InputGroupInput
                    id="price"
                    name="product[price]"
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder={t("shared.fields.price_placeholder")}
                    value={data.product.price}
                    onChange={(event) =>
                      setData((values) => ({
                        ...values,
                        product: {
                          ...values.product,
                          price: event.target.valueAsNumber,
                          price_cents: event.target.valueAsNumber * 100,
                        },
                      }))
                    }
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupText>
                      {current_store?.currency.toUpperCase()}
                    </InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              </Field>

              <Field>
                <FieldLabel htmlFor="stock">
                  {t("shared.fields.stock")}
                </FieldLabel>
                <Input
                  id="stock"
                  name="product[stock]"
                  type="number"
                  step="1"
                  min={0}
                  placeholder={t("shared.fields.stock_placeholder")}
                  value={data.product.stock}
                  onChange={(event) =>
                    setData("product.stock", event.target.valueAsNumber)
                  }
                />
              </Field>
            </FieldSet>

            <FieldSeparator />

            <FieldSet>
              <FieldLegend>{t("custom_fields_legend")}</FieldLegend>

              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="name">
                    {t("custom_name_label")}
                  </FieldLabel>
                  <Input
                    id="name"
                    name="product[name]"
                    placeholder={t("custom_name_placeholder")}
                    value={data.product.name ?? ""}
                    onChange={(event) =>
                      // @ts-ignore
                      setData("product.name", event.target.value)
                    }
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="custom_image">
                    {t("custom_image_label")}
                  </FieldLabel>
                  <Input
                    id="custom_image"
                    name="product[custom_image]"
                    type="file"
                    onChange={(event) =>
                      event.target.files &&
                      // @ts-ignore
                      setData("product.custom_image", event.target.files[0])
                    }
                  />
                </Field>
              </FieldGroup>
            </FieldSet>
          </FieldGroup>
        </form>

        <SheetFooter>
          <Button
            type="submit"
            form="new-product-form"
            disabled={
              newProductForm.processing ||
              !newProductForm.data.product.stock ||
              (!newProductForm.data.product.system_code &&
                !newProductForm.data.product.name)
            }
          >
            {newProductForm.processing && <Spinner />}
            {t("shared.actions.save")}
          </Button>
          <SheetClose asChild>
            <Button variant="outline" disabled={newProductForm.processing}>
              {t("shared.actions.close")}
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
